import type { AccountDetailPayload } from '../account-detail.model';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function coerceBalance(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isAbsentOrString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string';
}

function isOptionalFiniteNumber(value: unknown): boolean {
  return value === undefined || (typeof value === 'number' && Number.isFinite(value));
}

function isAbsentOrFiniteNumber(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

function isCardWire(value: unknown): boolean {
  return (
    isRecord(value) &&
    isAbsentOrString(value['currentState']) &&
    isAbsentOrString(value['cardNumberMasked']) &&
    isAbsentOrString(value['status'])
  );
}

function isLinkedWire(value: unknown): boolean {
  return (
    isRecord(value) &&
    isAbsentOrString(value['status']) &&
    isAbsentOrString(value['name']) &&
    isAbsentOrString(value['nickName']) &&
    isAbsentOrString(value['idMasked']) &&
    isAbsentOrString(value['accountNumberMasked'])
  );
}

function isAutoSaveSchedule(value: unknown): boolean {
  return (
    isRecord(value) &&
    isAbsentOrString(value['status']) &&
    isAbsentOrString(value['nextDepositDate']) &&
    isOptionalFiniteNumber(value['nextDepositAmount'])
  );
}

function isFundsSource(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }
  if (value['autoSaveSchedule'] !== undefined && !isAutoSaveSchedule(value['autoSaveSchedule'])) {
    return false;
  }
  return isAbsentOrString(value['type']) && isAbsentOrString(value['nextAvailableDate']);
}

const OPTIONAL_DATE_FIELDS = [
  'createdDate',
  'verifiedDate',
  'lastModifiedDate',
  'approvalDate',
  'statusUpdatedDate',
  'advanceTakenDate',
] as const;

const OPTIONAL_FINITE_NUMBER_FIELDS = ['amount', 'apr', 'loanTerm'] as const;

/** True when `value` is a usable GET /account/{type}/{id} payload. Extra keys allowed. */
export function isAccountDetailPayload(value: unknown): value is AccountDetailPayload {
  if (!isRecord(value)) {
    return false;
  }
  if (typeof value['id'] !== 'string' || !value['id']) {
    return false;
  }
  if (typeof value['name'] !== 'string') {
    return false;
  }
  if (typeof value['institution'] !== 'string') {
    return false;
  }
  if (typeof value['type'] !== 'string') {
    return false;
  }
  if (typeof value['status'] !== 'string') {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(value, 'balance')) {
    return false;
  }
  if (value['balance'] !== null && coerceBalance(value['balance']) === null) {
    return false;
  }
  if (!isOptionalString(value['idMasked'])) {
    return false;
  }
  if (OPTIONAL_DATE_FIELDS.some((field) => !isAbsentOrString(value[field]))) {
    return false;
  }
  if (OPTIONAL_FINITE_NUMBER_FIELDS.some((field) => !isAbsentOrFiniteNumber(value[field]))) {
    return false;
  }
  if (value['cards'] !== undefined) {
    if (!Array.isArray(value['cards']) || !value['cards'].every(isCardWire)) {
      return false;
    }
  }
  if (value['activeCard'] !== undefined && !isCardWire(value['activeCard'])) {
    return false;
  }
  if (value['linkedAccounts'] !== undefined) {
    if (!Array.isArray(value['linkedAccounts']) || !value['linkedAccounts'].every(isLinkedWire)) {
      return false;
    }
  }
  if (value['fundsSource'] !== undefined && !isFundsSource(value['fundsSource'])) {
    return false;
  }
  return true;
}

export function normalizeAccountDetailPayload(payload: AccountDetailPayload): AccountDetailPayload {
  return {
    ...payload,
    balance: coerceBalance(payload.balance),
  };
}
